import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { PageHeader, PageLoader, EmptyState } from '@/components/common/Layout';
import { RiskBadge } from '@/components/common/RiskBadge';
import { CategoryBadge } from '@/components/common/CategoryBadge';
import { ObservationStageBadge } from '@/components/common/ObservationStageBadge';
import { EvidenceBadge } from '@/components/common/EvidenceBadge';
import { SimulationDisclaimer } from '@/components/common/SimulationDisclaimer';
import { KpiCard } from '@/components/common/KpiCard';
import { AssetMap } from '@/components/map/AssetMap';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { api, downloadPdf } from '@/lib/api';
import { fmtDate, fmtRel, fmtPct, fmtCoord, ZONE_META, CATEGORY_META } from '@/lib/format';
import { fmtDistance, fmtFreshness } from '@/lib/format2';
import { useAuth, canManageAssets } from '@/lib/auth';
import { Landmark, MapPin, FileText, Layers, Edit, Satellite } from 'lucide-react';
import usePageTitle from '@/hooks/usePageTitle';
import { SkyFiObservations } from '@/components/asset/SkyFiObservations';

export default function AssetDetailPage() {
  const { assetId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [asset, setAsset] = useState(null);
  const [zones, setZones] = useState([]);
  const [summary, setSummary] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [obs, setObs] = useState([]);
  const [detections, setDetections] = useState([]);
  usePageTitle(asset ? `${asset.name} · Asset` : 'Asset');

  useEffect(() => {
    (async () => {
      const [a, z, s, al, inc, ob, dt] = await Promise.all([
        api.get(`/assets/${assetId}`).then(r => r.data),
        api.get('/zones', { params: { asset_id: assetId } }).then(r => r.data),
        api.get(`/assets/${assetId}/summary`).then(r => r.data),
        api.get('/alerts', { params: { asset_id: assetId, limit: 50 } }).then(r => r.data),
        api.get('/incidents', { params: { asset_id: assetId, limit: 50 } }).then(r => r.data),
        api.get('/observations', { params: { asset_id: assetId, limit: 50 } }).then(r => r.data),
        api.get('/detections', { params: { asset_id: assetId, limit: 50 } }).then(r => r.data),
      ]);
      setAsset(a); setZones(z); setSummary(s);
      setAlerts(al); setIncidents(inc); setObs(ob); setDetections(dt);
    })().catch(() => {});
  }, [assetId]);

  if (!asset || !summary) return <div className="p-6"><PageLoader label="Loading asset command view…" /></div>;

  const modules = ['water_change', 'terrain_disturbance', 'encroachment', 'fire_flood'];
  const modulesStatus = modules.map(m => {
    const dets = detections.filter(d => d.change_type === m);
    const latest = dets[0];
    return {
      key: m, count: dets.length, latest,
      confirmed: dets.filter(d => d.validation_status === 'Confirmed').length,
    };
  });

  const latestObs = obs[0];
  const freshness = fmtFreshness(latestObs?.acquired_at);

  return (
    <div className="p-6 max-w-[1600px] mx-auto" data-testid="asset-detail-root">
      <PageHeader
        title={asset.name}
        subtitle={<span className="flex flex-wrap items-center gap-1.5"><MapPin size={12} /> {asset.location_name} · <span className="font-mono">{fmtCoord(asset.lat)}, {fmtCoord(asset.lng)}</span>{asset.is_demonstration && <><span>·</span><SimulationDisclaimer compact /></>}</span>}
        actions={
          <div className="flex items-center gap-2">
            <RiskBadge severity={asset.current_risk_level} testId="asset-command-risk-score" />
            <Button
              variant="outline"
              data-testid="asset-report-btn"
              onClick={() => downloadPdf(`/reports/asset/${asset.id}/pdf`, `AakashDrishti_Asset_${asset.name.replace(/\s+/g, '_').slice(0, 24)}.pdf`)}
            >
              <FileText size={14} className="mr-1" /> Asset report
            </Button>
            {canManageAssets(user.role) && (
              <Button variant="outline" onClick={() => nav(`/app/assets/${assetId}/edit`)} data-testid="edit-asset-btn">
                <Edit size={14} className="mr-1" /> Edit
              </Button>
            )}
          </div>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard label="Zones" value={summary.zone_count} testId="asset-kpi-zones" />
        <KpiCard label="Observations" value={summary.observations_count} testId="asset-kpi-obs"
          hint={`Latest: ${freshness.label}`} tone={freshness.tone === 'warn' ? 'warn' : 'default'} />
        <KpiCard label="Active alerts" value={summary.active_alerts} tone="warn" testId="asset-kpi-alerts" />
        <KpiCard label="Open incidents" value={summary.open_incidents} tone="info" testId="asset-kpi-incidents" />
      </div>

      <div className="grid lg:grid-cols-3 gap-4 mt-4">
        <Card className="p-0 lg:col-span-2 overflow-hidden">
          <AssetMap
            asset={asset}
            zones={zones}
            subAssets={asset.sub_assets || []}
            alerts={alerts}
            incidents={incidents}
            height={520}
          />
        </Card>
        <Card className="p-4">
          <div className="text-sm font-semibold mb-3">Asset profile</div>
          <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
            <Row k="Category" v={asset.category} />
            <Row k="Criticality" v={asset.criticality} />
            <Row k="Owner" v={asset.owner || '—'} />
            <Row k="Commissioned" v={asset.commissioning_year || '—'} />
            <Row k="Region" v={asset.region || '—'} />
            <Row k="Status" v={asset.operational_status} />
          </div>
          {asset.hydropower && (
            <>
              <div className="mt-4 pt-3 border-t border-border text-[10px] uppercase tracking-widest text-muted-foreground">Hydropower details</div>
              <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs mt-2">
                <Row k="Dam type" v={asset.hydropower.dam_type || '—'} />
                <Row k="Dam height" v={asset.hydropower.dam_height_m ? `${asset.hydropower.dam_height_m} m` : '—'} />
                <Row k="Reservoir" v={asset.hydropower.reservoir_capacity_mcm ? `${asset.hydropower.reservoir_capacity_mcm} MCM` : '—'} />
                <Row k="Units" v={asset.hydropower.generating_units || '—'} />
                <Row k="Capacity" v={asset.hydropower.installed_capacity_mw ? `${asset.hydropower.installed_capacity_mw} MW` : '—'} />
                <Row k="Basin" v={asset.hydropower.river_basin || '—'} />
                <Row k="FRL" v={asset.hydropower.full_reservoir_level_m ? `${asset.hydropower.full_reservoir_level_m} m` : '—'} />
                <Row k="Catchment" v={asset.hydropower.catchment_area_km2 ? `${asset.hydropower.catchment_area_km2} km²` : '—'} />
              </div>
            </>
          )}
          {(asset.sub_assets || []).length > 0 && (
            <>
              <div className="mt-4 pt-3 border-t border-border text-[10px] uppercase tracking-widest text-muted-foreground">Sub-assets ({asset.sub_assets.length})</div>
              <div className="mt-2 space-y-1 text-xs">
                {asset.sub_assets.slice(0, 8).map((s, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <span>{s.name}</span>
                    <span className="text-muted-foreground font-mono text-[10px]">{fmtCoord(s.lat)}, {fmtCoord(s.lng)}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </Card>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
        {modulesStatus.map(m => (
          <Card key={m.key} className="p-4">
            <CategoryBadge category={m.key} />
            <div className="mt-2 text-2xl font-mono tabular-nums font-semibold">{m.count}</div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Observations</div>
            {m.latest && (
              <div className="mt-3 text-xs text-muted-foreground line-clamp-2">{m.latest.description}</div>
            )}
            {m.confirmed > 0 && <div className="mt-2 text-[10px] text-primary">{m.confirmed} confirmed by analyst</div>}
          </Card>
        ))}
      </div>

      <Tabs defaultValue="timeline" className="mt-6">
        <TabsList data-testid="asset-tabs">
          <TabsTrigger value="timeline">Timeline</TabsTrigger>
          <TabsTrigger value="skyfi" data-testid="tab-skyfi">
            <Satellite size={12} className="mr-1" /> Live satellite
          </TabsTrigger>
          <TabsTrigger value="zones">Zones ({zones.length})</TabsTrigger>
          <TabsTrigger value="alerts">Alerts ({alerts.length})</TabsTrigger>
          <TabsTrigger value="incidents">Incidents ({incidents.length})</TabsTrigger>
          <TabsTrigger value="observations">Observations ({obs.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="timeline">
          <Card className="p-4">
            <TimelineFeed obs={obs} detections={detections} alerts={alerts} incidents={incidents} />
          </Card>
        </TabsContent>
        <TabsContent value="skyfi">
          <SkyFiObservations asset={asset} />
        </TabsContent>
        <TabsContent value="zones">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {zones.length === 0 ? <EmptyState title="No zones defined" /> :
              zones.map(z => (
                <Card key={z.id} className="p-4" data-testid={`zone-card-${z.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="h-3 w-3 rounded-full" style={{ background: ZONE_META[z.zone_type]?.color }} />
                      <div className="font-medium text-sm">{z.name}</div>
                    </div>
                    <Badge variant="outline" className="text-[10px]">{ZONE_META[z.zone_type]?.label}</Badge>
                  </div>
                  <div className="mt-2 text-xs text-muted-foreground">{z.description}</div>
                  <div className="mt-2 flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">Risk sensitivity</span>
                    <RiskBadge severity={z.risk_category} />
                  </div>
                </Card>
              ))}
          </div>
        </TabsContent>
        <TabsContent value="alerts">
          <Card className="p-0"><AlertListRows items={alerts} /></Card>
        </TabsContent>
        <TabsContent value="incidents">
          <Card className="p-0"><IncidentListRows items={incidents} /></Card>
        </TabsContent>
        <TabsContent value="observations">
          <Card className="p-0"><ObsListRows items={obs} /></Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Row({ k, v }) {
  return (<><div className="text-muted-foreground">{k}</div><div className="text-right font-medium">{v}</div></>);
}

function TimelineFeed({ obs, detections, alerts, incidents }) {
  const events = [
    ...obs.map(o => ({ type: 'Observation', title: `${o.data_source} · ${o.sensor}`, ts: o.acquired_at, link: `/app/observations/${o.id}` })),
    ...detections.map(d => ({ type: 'Detection', title: `${CATEGORY_META[d.change_type]?.label || d.change_type} · risk ${d.risk_score}`, ts: d.created_at, link: `/app/detections/${d.id}`, severity: d.severity })),
    ...alerts.map(a => ({ type: 'Alert', title: a.title, ts: a.created_at, link: `/app/alerts/${a.id}`, severity: a.severity })),
    ...incidents.map(i => ({ type: 'Incident', title: `${i.number} · ${i.title}`, ts: i.created_at, link: `/app/incidents/${i.id}`, severity: i.severity })),
  ].sort((a, b) => (b.ts || '').localeCompare(a.ts || ''));
  if (events.length === 0) return <EmptyState title="No activity yet" />;
  return (
    <div className="relative pl-4" data-testid="asset-timeline">
      <div className="absolute left-1.5 top-1 bottom-1 w-px bg-border" />
      <ul className="space-y-3">
        {events.slice(0, 40).map((e, i) => (
          <li key={i} className="relative" data-testid="timeline-event">
            <span className="absolute -left-2.5 top-1.5 h-2.5 w-2.5 rounded-full bg-primary" />
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{e.type} · {fmtDate(e.ts)}</div>
            <Link to={e.link} className="text-sm font-medium hover:text-primary">{e.title}</Link>
            {e.severity && <span className="ml-2"><RiskBadge severity={e.severity} /></span>}
          </li>
        ))}
      </ul>
    </div>
  );
}

function AlertListRows({ items }) {
  if (!items.length) return <EmptyState title="No alerts" />;
  return (
    <div className="divide-y divide-border">
      {items.map(a => (
        <Link key={a.id} to={`/app/alerts/${a.id}`} className={`flex items-center gap-3 p-3 hover:bg-secondary/60 risk-stripe risk-stripe-${a.severity?.toLowerCase()}`}>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium truncate">{a.title}</div>
            <div className="text-xs text-muted-foreground">{fmtRel(a.created_at)} · {a.status}</div>
          </div>
          <CategoryBadge category={a.category} />
          <RiskBadge severity={a.severity} />
        </Link>
      ))}
    </div>
  );
}

function IncidentListRows({ items }) {
  if (!items.length) return <EmptyState title="No incidents" />;
  return (
    <div className="divide-y divide-border">
      {items.map(i => (
        <Link key={i.id} to={`/app/incidents/${i.id}`} className={`flex items-center gap-3 p-3 hover:bg-secondary/60 risk-stripe risk-stripe-${i.severity?.toLowerCase()}`}>
          <span className="font-mono text-xs text-muted-foreground w-24 shrink-0">{i.number}</span>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium truncate">{i.title}</div>
            <div className="text-xs text-muted-foreground">{i.status} · {fmtRel(i.created_at)}</div>
          </div>
          <RiskBadge severity={i.severity} />
        </Link>
      ))}
    </div>
  );
}

function ObsListRows({ items }) {
  if (!items.length) return <EmptyState title="No observations" />;
  return (
    <div className="divide-y divide-border">
      {items.map(o => (
        <Link key={o.id} to={`/app/observations/${o.id}`} className="flex items-center gap-3 p-3 hover:bg-secondary/60">
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium">{o.data_source} · {o.sensor}</div>
            <div className="text-xs text-muted-foreground font-mono">{fmtDate(o.acquired_at)}</div>
          </div>
          <Badge variant="outline" className="text-[10px]">Cloud {fmtPct(o.cloud_coverage_pct, 0)}</Badge>
          <Badge variant="outline" className="text-[10px]">{o.quality}</Badge>
        </Link>
      ))}
    </div>
  );
}
